import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

const listItemSx = (active, hasRisk) => ({
  position: 'relative',
  p: 1,
  borderRadius: 1.25,
  border: '1px solid',
  borderColor: active ? 'primary.main' : hasRisk ? 'warning.light' : 'divider',
  bgcolor: active ? 'primary.50' : 'background.paper',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&::before': hasRisk
    ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        borderRadius: '4px 0 0 4px',
        bgcolor: active ? 'warning.main' : 'warning.light',
      }
    : undefined,
  '&:hover': {
    borderColor: active ? 'primary.main' : 'primary.light',
    bgcolor: active ? 'primary.100' : 'action.hover',
  },
});

const alertChip = (label, color) => (
  <Chip
    key={label}
    label={label}
    color={color}
    size="small"
    sx={{ height: 17, '& .MuiChip-label': { px: 0.6, fontSize: '0.62rem' } }}
  />
);

export default function PaymentInvoiceList({
  invoices = [],
  activeInvoiceId = null,
  onSelect,
  formatCurrency,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 420,
      }}
    >
      <Box sx={{ px: 1.2, py: 0.95, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          Facturas seleccionadas
        </Typography>
      </Box>
      <Box sx={{ p: 0.85, overflowY: 'auto', maxHeight: 560 }}>
        <Stack spacing={0.6}>
          {invoices.map((item) => {
            const active = Number(activeInvoiceId) === Number(item.id);
            const hasRisk = item.alerts.review || item.alerts.shortage || item.alerts.priceDiff;
            return (
              <Box
                key={item.id}
                sx={listItemSx(active, hasRisk)}
                onClick={() => onSelect?.(item.id)}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box sx={{ minWidth: 0, pr: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, fontSize: '0.76rem', lineHeight: 1.15 }}
                    >
                      #{item.number}{item.series ? ` - ${item.series}` : ''}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.66rem',
                        display: 'block',
                        maxWidth: 170,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.supplier || '-'}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{ fontWeight: 700, fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatCurrency(item.finalPayable || 0)}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 0.55 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem' }}>
                    Saldo: {formatCurrency(item.originalBalance || 0)}
                  </Typography>
                  <Typography variant="caption" color="success.main" sx={{ fontSize: '0.64rem' }}>
                    Ajustes: -{formatCurrency(item.appliedAdjustments || 0)}
                  </Typography>
                  <Typography variant="caption" color="info.main" sx={{ fontSize: '0.64rem' }}>
                    NC/Abono: -{formatCurrency((item.creditNotes || 0) + (item.previousPayments || 0))}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.4} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
                  {item.alerts.shortage ? alertChip('Faltante', 'warning') : null}
                  {item.alerts.priceDiff ? alertChip('Dif precio', 'error') : null}
                  {item.alerts.credit ? alertChip('NC', 'info') : null}
                  {item.alerts.review ? alertChip('Revisar', 'default') : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
}